import { useState, useRef, useCallback } from 'react'
import { Send, Paperclip, X, FileText } from 'lucide-react'

interface AttachmentPreview {
  file: File
  preview?: string  // data URL for images
}

interface Props {
  onSend: (message: string, attachments?: File[]) => void
  disabled?: boolean
}

const MAX_FILES = 5
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ACCEPTED_TYPES = 'image/*,.pdf,.txt,.csv,.json,.md,.yaml,.yml,.xml,.log'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function ChatInput({ onSend, disabled }: Props) {
  const [value, setValue] = useState('')
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const [dragOver, setDragOver] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles = Array.from(files)

    // Validate
    setAttachments(prev => {
      const remaining = MAX_FILES - prev.length
      if (remaining <= 0) return prev

      const toAdd: AttachmentPreview[] = []
      for (const file of newFiles.slice(0, remaining)) {
        if (file.size > MAX_FILE_SIZE) {
          alert(`${file.name} exceeds 20MB limit`)
          continue
        }
        const isImage = file.type.startsWith('image/')
        const preview = isImage ? URL.createObjectURL(file) : undefined
        toAdd.push({ file, preview })
      }
      return [...prev, ...toAdd]
    })
  }, [])

  const removeAttachment = useCallback((index: number) => {
    setAttachments(prev => {
      const removed = prev[index]
      if (removed?.preview) URL.revokeObjectURL(removed.preview)
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if ((!trimmed && attachments.length === 0) || disabled) return
    onSend(trimmed, attachments.length > 0 ? attachments.map(a => a.file) : undefined)
    setValue('')
    // Revoke preview URLs
    attachments.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview) })
    setAttachments([])
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, attachments, disabled, onSend])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = () => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 120) + 'px'
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => setDragOver(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }

  return (
    <div className="border-t border-gray-100 px-4 py-3 bg-white">
      {/* Attachment preview strip */}
      {attachments.length > 0 && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {attachments.map((att, i) => (
            <div
              key={i}
              className="relative group flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5"
            >
              {att.preview ? (
                <img
                  src={att.preview}
                  alt={att.file.name}
                  className="w-8 h-8 rounded object-cover"
                />
              ) : (
                <FileText className="w-4 h-4 text-gray-400" />
              )}
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-gray-700 truncate max-w-[100px]">
                  {att.file.name}
                </div>
                <div className="text-[10px] text-gray-400">
                  {formatFileSize(att.file.size)}
                </div>
              </div>
              <button
                onClick={() => removeAttachment(i)}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {attachments.length >= MAX_FILES && (
            <span className="text-[10px] text-gray-400">Max {MAX_FILES} files</span>
          )}
        </div>
      )}

      <div
        className={`flex items-end gap-2 ${dragOver ? 'ring-2 ring-primary-400 rounded-xl' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Paperclip button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || attachments.length >= MAX_FILES}
          className="w-9 h-9 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          title="Attach files"
        >
          <Paperclip className="w-4 h-4" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = '' // reset so same file can be re-selected
          }}
          className="hidden"
        />

        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              handleInput()
            }}
            onKeyDown={handleKeyDown}
            placeholder={attachments.length > 0 ? 'Add a message about these files...' : 'Ask about GitLab, compliance, features...'}
            rows={1}
            disabled={disabled}
            className="w-full resize-none rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-gray-400 disabled:opacity-50 transition-colors max-h-[120px]"
          />
        </div>
        <button
          onClick={handleSend}
          disabled={disabled || (!value.trim() && attachments.length === 0)}
          className={`w-9 h-9 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-all flex items-center justify-center shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
            disabled ? 'animate-pulse' : ''
          }`}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center justify-between mt-1.5 px-1">
        <span className="text-[10px] text-gray-400">
          Press Enter to send, Shift+Enter for new line
        </span>
        <span className="text-[10px] text-gray-400">Claude Sonnet 4</span>
      </div>
    </div>
  )
}
